import { Component, inject, OnInit } from '@angular/core';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { Router } from '@angular/router';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { CardComponent, TableService } from '@web/birdz-angular';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GetRowIdParams, GridOptions, ValueFormatterParams } from 'ag-grid-community';
import { BigQueryUsageStat, UsagePeriod } from 'app/interfaces/bigquery-usage.interface';
import { QueryService } from 'app/services/query.service';
import { formatBytes } from 'app/utils/format-bytes.utils';

interface PeriodOption {
  value: UsagePeriod;
  label: string;
}

@Component({
  selector: 'app-page-bigquery-usage',
  templateUrl: './page-bigquery-usage.component.html',
  styleUrls: ['./page-bigquery-usage.component.scss'],
  imports: [CardComponent, AgGridAngular, MatButtonToggleModule, TranslocoPipe],
})
export class PageBigqueryUsageComponent implements OnInit {
  private queryService = inject(QueryService);
  private table = inject(TableService);
  private router = inject(Router);
  private transloco = inject(TranslocoService);

  isLoading = false;
  period: UsagePeriod = '31d';

  periods: PeriodOption[] = [
    { value: '24h', label: 'BQ_PERIOD_24H' },
    { value: '31d', label: 'BQ_PERIOD_31D' },
    { value: '365d', label: 'BQ_PERIOD_365D' },
  ];

  gridOptions: GridOptions = this.table.getDefaultGridOptions('clientSide', {
    suppressMovableColumns: true,
    getRowId: (params: GetRowIdParams<BigQueryUsageStat>) => params.data.query_id,
  });
  agColumns: ColDef[] = [];
  agRows: BigQueryUsageStat[] = [];

  ngOnInit(): void {
    this.buildColumns();
    this.loadData();
  }

  selectPeriod(period: UsagePeriod): void {
    this.period = period;
    this.loadData();
  }

  private loadData(): void {
    this.isLoading = true;
    this.queryService.getBigQueryUsage(this.period).subscribe({
      next: (rows) => {
        this.agRows = rows;
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private buildColumns(): void {
    const bytesFormatter = (params: ValueFormatterParams) => formatBytes(params.value as number) ?? '';

    this.agColumns = [
      {
        field: 'query_name',
        headerName: this.transloco.translate('BQ_COL_QUERY'),
        sortable: true,
        flex: 2,
        cellRenderer: (params: { data: BigQueryUsageStat; value: string }) => {
          const a = document.createElement('a');
          a.href = `/query/${params.data.query_id}`;
          a.textContent = params.value;
          a.addEventListener('click', (e) => {
            e.preventDefault();
            this.router.navigate(['/query', params.data.query_id]);
          });
          return a;
        },
      },
      {
        field: 'total_bytes',
        headerName: this.transloco.translate('BQ_COL_TOTAL'),
        sortable: true,
        sort: 'desc',
        flex: 1,
        valueFormatter: bytesFormatter,
      },
      {
        field: 'execution_count',
        headerName: this.transloco.translate('BQ_COL_COUNT'),
        sortable: true,
        flex: 1,
      },
      {
        field: 'avg_bytes',
        headerName: this.transloco.translate('BQ_COL_AVG'),
        sortable: true,
        flex: 1,
        valueFormatter: bytesFormatter,
      },
    ];
  }
}
